import { PanelLeftIcon, PanelRightIcon } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

import { PanelShell } from '../PanelShell';

export interface PanelToggleProps {
  /** Which side's panel this opens. Picks the icon, which is the only thing the control shows. */
  side: 'left' | 'right';
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** One string for the tooltip and the accessible name, so the two cannot drift apart. */
  label: string;
  /**
   * `inline` sits in an open panel's header, which already provides the surface under it.
   * `floating` is the collapsed state, where there is no panel left to sit in: the toggle
   * becomes a panel surface of its own, standing on the board.
   */
  appearance?: 'inline' | 'floating';
  'data-testid'?: string;
}

const ICONS = { left: PanelLeftIcon, right: PanelRightIcon };

/**
 * The one control that opens and closes a side panel, in either of the two places it has to
 * stand: inside the open panel's header, and alone on the board once the panel is collapsed
 * away. Both states come from here so the two sides of a screen cannot drift apart - which is
 * exactly what happened while each panel rolled its own.
 */
export function PanelToggle({
  side,
  isOpen,
  onOpenChange,
  label,
  appearance = 'inline',
  'data-testid': testId,
}: PanelToggleProps) {
  const Icon = ICONS[side];

  const control = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            aria-expanded={isOpen}
            data-testid={testId}
            onClick={() => onOpenChange(!isOpen)}
          >
            <Icon className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (appearance === 'inline') return control;

  // A shadow where the other panels carry none: those are separated by the page ground showing
  // through the gaps, while this one overlaps the board and has to lift off it. `p-2` keeps the
  // card at the width the collapsed rail already had, so collapsing loses the height, not the
  // column.
  return (
    <PanelShell data-testid="panel-toggle-surface" className="p-2 shadow-md">
      {control}
    </PanelShell>
  );
}
